import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { NetworkProvider, NetworkStatus } from '../../../providers/network/network';
import { PortefeuilleService } from 'src/app/shared/providers/portefeuille/portefeuille.service';
import { State } from 'src/app/shared/models/state.model';
import { UserService } from '../../../providers/auth/user.service';
import { SitesFavorisProvider } from '../../../providers/sites-favoris/sites-favoris.provider';
import { GetSitePortefeuilleResponseDto } from '../../../../api/models/get-site-portefeuille-response-dto';
import { isTogglePortefeuillePerimetreIsoleEnabled } from '../../../utils/toggle-utils';

@Component({
  selector: 'sites-favoris-widget',
  templateUrl: 'sites-favoris-widget.component.html',
  styleUrls: ['sites-favoris-widget.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TranslateModule],
})
export class SitesFavorisWidgetComponent implements OnInit, OnDestroy {
  @Input() resetSubject: Observable<boolean> = new Observable<boolean>();

  siteLoadedSubject: Subscription = null;
  networkStatusSubject: Subscription = null;
  isOnline: boolean = true;
  showAllSites: boolean = false;
  portefeuille: GetSitePortefeuilleResponseDto[] = [];
  sitesFavoris: GetSitePortefeuilleResponseDto[] = [];
  sitesFavorisToDisplay: GetSitePortefeuilleResponseDto[] = [];
  starredSites = new Set<string>();

  allStates = State;
  state = signal<State>(State.LOADING);

  constructor(
    private router: Router,
    private userService: UserService,
    private portefeuilleService: PortefeuilleService,
    private networkProvider: NetworkProvider,
    private sitesFavorisProvider: SitesFavorisProvider
  ) {}
  async ngOnInit() {
    await this.startCheckConnection();

    this.resetSubject.subscribe((response) => {
      if (response) {
        this.refresh();
      }
    });
    this.siteLoadedSubject = this.sitesFavorisProvider.isSitesLoaded$.subscribe((isSitesLoaded) => {
      if (isSitesLoaded) {
        this.refresh();
      }
    });
  }

  private async startCheckConnection() {
    this.networkStatusSubject = this.networkProvider.networkStatusSubject.subscribe((networkStatus) => {
      let oldIsOnline = this.isOnline;
      this.isOnline = networkStatus === NetworkStatus.ONLINE;
      if (!oldIsOnline && this.isOnline) {
        this.refresh();
      }
    });
  }

  async refresh() {
    if (!this.isOnline) {
      this.state.set(State.ERROR);
      return;
    }
    this.state.set(State.LOADING);

    try {
      await this.loadSiteFavoris();
      this.loadSiteFavorisToDisplay();
      this.state.set(State.SUCCESS);
    } catch (err) {
      console.error('Error: ', err);
      this.state.set(State.ERROR);
    }
  }

  async loadSiteFavoris() {
    const sitesFavoris = [];
    const currentUser = this.userService.getCurrentUser();
    this.starredSites = await this.sitesFavorisProvider.getSitesFavoris();
    if (!this.starredSites || this.starredSites.size == 0) {
      return;
    }

    if (isTogglePortefeuillePerimetreIsoleEnabled()) {
      this.portefeuille = await this.portefeuilleService.getPortefeuillePerimetreIsole(currentUser?.id);
    } else {
      this.portefeuille = await this.portefeuilleService.getPortefeuille(
        currentUser?.id,
        Array.from(this.starredSites)
      );
    }

    for (let site_portefeuille of this.portefeuille) {
      if (this.starredSites.has(site_portefeuille.id_site)) {
        sitesFavoris.push(site_portefeuille);
      }
    }
    sitesFavoris.sort((a, b) => a.libelle_long.localeCompare(b.libelle_long));

    this.sitesFavoris = sitesFavoris;
  }

  loadSiteFavorisToDisplay() {
    this.sitesFavorisToDisplay = [];
    if (this.showAllSites) {
      this.sitesFavorisToDisplay = this.sitesFavoris;
    } else {
      this.sitesFavorisToDisplay = this.sitesFavoris.slice(0, 5);
    }
  }

  changeDisplay() {
    this.showAllSites = !this.showAllSites;
    this.loadSiteFavorisToDisplay();
  }

  async showDetail(site: GetSitePortefeuilleResponseDto) {
    await this.router.navigate(['/mnt/sites', site.id_site]);
  }

  async goToPortefeuille(): Promise<void> {
    if (this.isOnline) {
      await this.router.navigate(['/mnt/sites'], {
        state: { segments: 'portefeuille' },
      });
    }
  }

  ngOnDestroy() {
    this.networkStatusSubject.unsubscribe();
    this.siteLoadedSubject.unsubscribe();
  }
}
